import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { getUserProfile } from '../../utils/storage';
import {
  PERMISSIONS,
  getGaragePermissions,
  inviteCollaborator,
  updateCollaboratorPermission,
  removeCollaborator,
  leaveGarage,
} from '../../services/permissionService';
import { getSubscriptionLimits } from '../../utils/storage';

/* ─── Constants ─── */

const BADGE_CONFIG = {
  full_access:    { label: 'Full Access',    bg: 'rgba(211,71,74,0.15)', color: '#D3474A' },
  service_access: { label: 'Service Access', bg: 'rgba(59,130,246,0.15)', color: '#3B82F6' },
  log_access:     { label: 'Log Access',     bg: 'rgba(234,179,8,0.15)',  color: '#EAB308' },
  view_only:      { label: 'View Only',      bg: 'rgba(156,163,175,0.15)', color: '#9CA3AF' },
};

const PERMISSION_OPTIONS = [
  { value: PERMISSIONS.FULL_ACCESS, label: 'Full Access' },
  { value: PERMISSIONS.SERVICE_ACCESS, label: 'Service Access' },
  { value: PERMISSIONS.LOG_ACCESS, label: 'Log Access' },
  { value: PERMISSIONS.VIEW_ONLY, label: 'View Only' },
];

/* ─── Component ─── */

export default function GarageSettingsModal({
  garage,
  vehicleCount,
  onUpdate,
  onDelete,
  onClose,
  onToast,
}) {
  const { user } = useAuth();
  const profile = getUserProfile();

  // Garage fields
  const [name, setName] = useState(garage.name);
  const [location, setLocation] = useState(garage.location || '');
  const [collaborators, setCollaborators] = useState(garage.collaborators || []);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Permission state
  const [permLoading, setPermLoading] = useState(true);
  const [ownerIdFromDb, setOwnerIdFromDb] = useState(null);
  const [permissionsMap, setPermissionsMap] = useState({});

  // Invite modal state
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [invitePermission, setInvitePermission] = useState(PERMISSIONS.VIEW_ONLY);
  const [inviteMessage, setInviteMessage] = useState('');
  const [inviteLoading, setInviteLoading] = useState(false);

  // Loading state for individual collaborator actions
  const [updatingId, setUpdatingId] = useState(null);

  // Leave garage state (non-owners)
  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);
  const [leaveLoading, setLeaveLoading] = useState(false);

  // Load permissions from Supabase on mount
  useEffect(() => {
    let cancelled = false;
    async function loadPermissions() {
      if (!user) { setPermLoading(false); return; }
      const result = await getGaragePermissions(garage.id);
      if (cancelled) return;
      setOwnerIdFromDb(result.owner_id);
      setPermissionsMap(result.collaborator_permissions || {});
      setPermLoading(false);
    }
    loadPermissions();
    return () => { cancelled = true; };
  }, [garage.id, user]);

  // Derived permission state
  const isOwner = ownerIdFromDb ? ownerIdFromDb === user?.id : garage.owner === user?.email;
  const canManageTeam = isOwner;

  // Build collaborator entries from permissionsMap for display
  const collabEntries = Object.entries(permissionsMap).map(([id, entry]) => ({
    id,
    email: entry.email,
    permission: entry.permission || PERMISSIONS.VIEW_ONLY,
    grantedAt: entry.grantedAt,
  }));

  // Also include legacy collaborators not in permissionsMap
  const permEmails = new Set(collabEntries.map((c) => c.email?.toLowerCase()));
  const legacyEntries = collaborators
    .filter((c) => !permEmails.has(c.email?.toLowerCase()))
    .map((c) => ({
      id: `legacy:${c.email}`,
      email: c.email,
      permission: PERMISSIONS.VIEW_ONLY,
      grantedAt: c.addedAt,
      isLegacy: true,
    }));

  const allCollaborators = [...collabEntries, ...legacyEntries];

  /* ─── Handlers ─── */

  function handleSave() {
    onUpdate(garage.id, { name: name.trim(), location: location.trim(), collaborators });
    onToast('Garage updated');
    onClose();
  }

  async function handleInvite(e) {
    e.preventDefault();
    if (!inviteEmail.trim()) return;
    setInviteLoading(true);

    const result = await inviteCollaborator(
      garage.id, user.id, inviteEmail.trim(), invitePermission, inviteMessage.trim() || null
    );
    if (result.error) {
      onToast(result.error);
    } else {
      // Invitation is now pending — no local collaborator update needed
      onToast(`Invitation sent to ${inviteEmail.trim()}`);
      setShowInviteModal(false);
      setInviteEmail('');
      setInvitePermission(PERMISSIONS.VIEW_ONLY);
      setInviteMessage('');
    }
    setInviteLoading(false);
  }

  async function handlePermissionChange(collaboratorId, newPermission) {
    setUpdatingId(collaboratorId);
    const result = await updateCollaboratorPermission(garage.id, user.id, collaboratorId, newPermission);
    if (result.error) {
      onToast(result.error);
    } else {
      // Update local permissionsMap
      if (result.data?.collaborator_permissions) {
        setPermissionsMap(result.data.collaborator_permissions);
      }
      onToast('Permission updated');
    }
    setUpdatingId(null);
  }

  async function handleRemoveCollab(collaboratorId) {
    setUpdatingId(collaboratorId);
    const result = await removeCollaborator(garage.id, user.id, collaboratorId);
    if (result.error) {
      onToast(result.error);
    } else {
      if (result.data?.collaborators) {
        setCollaborators(result.data.collaborators);
        onUpdate(garage.id, { collaborators: result.data.collaborators });
      }
      if (result.data?.collaborator_permissions) {
        setPermissionsMap(result.data.collaborator_permissions);
      }
      onToast('Collaborator removed');
    }
    setUpdatingId(null);
  }

  function handleDelete() {
    onDelete(garage.id);
    onClose();
  }

  async function handleLeave() {
    setLeaveLoading(true);
    const result = await leaveGarage(garage.id, user.id, user.email);
    if (result.error) {
      onToast(result.error);
      setLeaveLoading(false);
    } else {
      onToast('Left garage');
      onDelete(garage.id); // Remove from local state
      onClose();
    }
  }

  /* ─── Sub-components ─── */

  function PermissionBadge({ permission }) {
    const config = BADGE_CONFIG[permission] || BADGE_CONFIG.view_only;
    return (
      <span
        style={{
          display: 'inline-block',
          padding: '3px 8px',
          borderRadius: 'var(--radius-sm)',
          fontSize: '0.6875rem',
          fontWeight: 600,
          letterSpacing: '0.02em',
          background: config.bg,
          color: config.color,
          whiteSpace: 'nowrap',
        }}
      >
        {config.label}
      </span>
    );
  }

  /* ─── Render ─── */

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.7)',
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        zIndex: 200,
        padding: 20,
        paddingTop: 60,
        overflowY: 'auto',
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--bg-card)',
          borderRadius: 'var(--radius-md)',
          padding: 24,
          width: '100%',
          maxWidth: 400,
          border: '1px solid var(--border)',
        }}
      >
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h2 style={{ fontSize: '1.125rem', fontWeight: 700 }}>Garage Settings</h2>
          <button
            onClick={onClose}
            style={{
              width: 28,
              height: 28,
              borderRadius: '50%',
              background: 'var(--bg-elevated)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'var(--text-muted)',
              fontSize: '1rem',
            }}
          >
            x
          </button>
        </div>

        {/* Rename */}
        <div className="form-group">
          <label className="form-label">Garage Name</label>
          <input
            className="form-input"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>

        {/* Location */}
        <div className="form-group">
          <label className="form-label">Location (optional)</label>
          <input
            className="form-input"
            type="text"
            placeholder="e.g. 123 Main St, Austin TX"
            value={location}
            onChange={(e) => setLocation(e.target.value)}
          />
        </div>

        {/* ─── Team Members Section ─── */}
        <div className="form-group">
          <label className="form-label">Team Members</label>
          <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: 12 }}>
            {canManageTeam
              ? 'Manage who has access to this garage and their permissions.'
              : 'You can view team members but only the owner can manage access.'}
          </p>

          {permLoading ? (
            <div style={{ textAlign: 'center', padding: '16px 0', color: 'var(--text-muted)', fontSize: '0.8125rem' }}>
              Loading team...
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {/* Owner row — always first */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '10px 12px',
                  background: 'var(--bg-elevated)',
                  borderRadius: 'var(--radius-sm)',
                  border: '1px solid rgba(211,71,74,0.2)',
                }}
              >
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
                    <span style={{ fontSize: '0.8125rem', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {profile?.firstName && profile?.lastName
                        ? `${profile.firstName} ${profile.lastName}`
                        : user?.email || garage.owner}
                      {isOwner ? ' (you)' : ''}
                    </span>
                  </div>
                  <div style={{ fontSize: '0.6875rem', color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {user?.email || garage.owner}
                  </div>
                </div>
                <span
                  style={{
                    display: 'inline-block',
                    padding: '3px 10px',
                    borderRadius: 'var(--radius-sm)',
                    fontSize: '0.6875rem',
                    fontWeight: 700,
                    letterSpacing: '0.02em',
                    background: 'rgba(211,71,74,0.15)',
                    color: '#D3474A',
                    whiteSpace: 'nowrap',
                    flexShrink: 0,
                  }}
                >
                  Owner
                </span>
              </div>

              {/* Collaborator rows */}
              {allCollaborators.map((collab) => (
                <div
                  key={collab.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '10px 12px',
                    background: 'var(--bg-elevated)',
                    borderRadius: 'var(--radius-sm)',
                    gap: 8,
                    opacity: updatingId === collab.id ? 0.6 : 1,
                    transition: 'opacity 0.2s',
                  }}
                >
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ fontSize: '0.8125rem', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginBottom: 4 }}>
                      {collab.email}
                    </div>

                    {/* Permission: dropdown if owner, badge if read-only */}
                    {canManageTeam && !collab.isLegacy ? (
                      <select
                        value={collab.permission}
                        onChange={(e) => handlePermissionChange(collab.id, e.target.value)}
                        disabled={updatingId === collab.id}
                        style={{
                          fontSize: '0.6875rem',
                          fontWeight: 600,
                          padding: '3px 6px',
                          borderRadius: 'var(--radius-sm)',
                          border: '1px solid var(--border)',
                          background: 'var(--bg-card)',
                          color: BADGE_CONFIG[collab.permission]?.color || 'var(--text-secondary)',
                          cursor: 'pointer',
                          appearance: 'auto',
                        }}
                      >
                        {PERMISSION_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    ) : (
                      <PermissionBadge permission={collab.permission} />
                    )}
                  </div>

                  {/* Remove button (owner only) */}
                  {canManageTeam && (
                    <button
                      onClick={() => handleRemoveCollab(collab.id)}
                      disabled={updatingId === collab.id}
                      style={{
                        fontSize: '0.75rem',
                        color: 'var(--danger)',
                        fontWeight: 600,
                        padding: '4px 8px',
                        flexShrink: 0,
                        opacity: updatingId === collab.id ? 0.4 : 1,
                        cursor: updatingId === collab.id ? 'wait' : 'pointer',
                      }}
                    >
                      {updatingId === collab.id ? '...' : 'Remove'}
                    </button>
                  )}
                </div>
              ))}

              {/* Empty state */}
              {allCollaborators.length === 0 && (
                <p style={{ fontSize: '0.8125rem', color: 'var(--text-muted)', textAlign: 'center', padding: '12px 0' }}>
                  No team members yet
                </p>
              )}
            </div>
          )}

          {/* Invite button (owner only + Team plan) */}
          {canManageTeam && !permLoading && getSubscriptionLimits().canShare && (
            <button
              onClick={() => setShowInviteModal(true)}
              style={{
                width: '100%',
                marginTop: 10,
                padding: '10px 16px',
                fontSize: '0.8125rem',
                fontWeight: 600,
                borderRadius: 'var(--radius-sm)',
                border: '1.5px dashed var(--border)',
                background: 'transparent',
                color: 'var(--text-secondary)',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 6,
              }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="12" y1="5" x2="12" y2="19" />
                <line x1="5" y1="12" x2="19" y2="12" />
              </svg>
              Invite Team Member
            </button>
          )}
        </div>

        {/* Save Button */}
        <button
          className="btn btn-primary"
          onClick={handleSave}
          disabled={!name.trim()}
          style={{ marginBottom: 16 }}
        >
          Save Changes
        </button>

        {/* Leave Garage — non-owners only */}
        {!isOwner && !permLoading && (
          <div style={{ borderTop: '1px solid var(--border)', paddingTop: 16, marginBottom: 16 }}>
            {showLeaveConfirm ? (
              <>
                <p style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)', marginBottom: 12, lineHeight: 1.4 }}>
                  Leave &ldquo;{garage.name}&rdquo;? You will lose access to all vehicles and data in this garage.
                </p>
                <div style={{ display: 'flex', gap: 10 }}>
                  <button
                    className="btn btn-secondary"
                    onClick={() => setShowLeaveConfirm(false)}
                    disabled={leaveLoading}
                    style={{ flex: 1 }}
                  >
                    Cancel
                  </button>
                  <button
                    className="btn btn-primary"
                    onClick={handleLeave}
                    disabled={leaveLoading}
                    style={{ flex: 1, background: 'var(--danger)', opacity: leaveLoading ? 0.7 : 1 }}
                  >
                    {leaveLoading ? 'Leaving...' : 'Leave Garage'}
                  </button>
                </div>
              </>
            ) : (
              <button
                onClick={() => setShowLeaveConfirm(true)}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  borderRadius: 'var(--radius-sm)',
                  border: '1.5px solid var(--danger)',
                  color: 'var(--danger)',
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  background: 'transparent',
                  cursor: 'pointer',
                }}
              >
                Leave Garage
              </button>
            )}
          </div>
        )}

        {/* Danger Zone — Delete Garage (owner only) */}
        {canManageTeam && (
          <div style={{ borderTop: '1px solid var(--border)', paddingTop: 16 }}>
            {showDeleteConfirm ? (
              <>
                <p style={{ fontSize: '0.8125rem', color: 'var(--danger)', marginBottom: 12, lineHeight: 1.4 }}>
                  Delete &ldquo;{garage.name}&rdquo; and its {vehicleCount} {vehicleCount === 1 ? 'vehicle' : 'vehicles'}? This cannot be undone.
                </p>
                <div style={{ display: 'flex', gap: 10 }}>
                  <button
                    className="btn btn-secondary"
                    onClick={() => setShowDeleteConfirm(false)}
                    style={{ flex: 1 }}
                  >
                    Cancel
                  </button>
                  <button
                    className="btn btn-primary"
                    onClick={handleDelete}
                    style={{ flex: 1, background: 'var(--danger)' }}
                  >
                    Delete
                  </button>
                </div>
              </>
            ) : (
              <button
                onClick={() => setShowDeleteConfirm(true)}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  borderRadius: 'var(--radius-sm)',
                  border: '1.5px solid var(--danger)',
                  color: 'var(--danger)',
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  background: 'transparent',
                }}
              >
                Delete Garage
              </button>
            )}
          </div>
        )}
      </div>

      {/* ─── Invite Team Member Modal (overlay on top) ─── */}
      {showInviteModal && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 210,
            padding: 20,
          }}
          onClick={() => setShowInviteModal(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'var(--bg-card)',
              borderRadius: 'var(--radius-md)',
              padding: 24,
              width: '100%',
              maxWidth: 360,
              border: '1px solid var(--border)',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h3 style={{ fontSize: '1rem', fontWeight: 700 }}>Invite Team Member</h3>
              <button
                onClick={() => setShowInviteModal(false)}
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: '50%',
                  background: 'var(--bg-elevated)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'var(--text-muted)',
                  fontSize: '1rem',
                }}
              >
                x
              </button>
            </div>

            <form onSubmit={handleInvite}>
              {/* Email */}
              <div style={{ marginBottom: 12 }}>
                <label style={labelStyle}>Email Address</label>
                <input
                  className="form-input"
                  type="email"
                  placeholder="colleague@example.com"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  required
                  autoFocus
                />
              </div>

              {/* Permission level */}
              <div style={{ marginBottom: 12 }}>
                <label style={labelStyle}>Permission Level</label>
                <select
                  className="form-input"
                  value={invitePermission}
                  onChange={(e) => setInvitePermission(e.target.value)}
                  style={{ cursor: 'pointer', appearance: 'auto' }}
                >
                  {PERMISSION_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
                <p style={{ fontSize: '0.6875rem', color: 'var(--text-muted)', marginTop: 4 }}>
                  {invitePermission === PERMISSIONS.FULL_ACCESS && 'Can manage team, delete vehicles, edit, diagnose, log, and view.'}
                  {invitePermission === PERMISSIONS.SERVICE_ACCESS && 'Can run diagnostics, edit vehicles, log service, and view.'}
                  {invitePermission === PERMISSIONS.LOG_ACCESS && 'Can log service entries and mileage, and view vehicles.'}
                  {invitePermission === PERMISSIONS.VIEW_ONLY && 'Can only view garage and vehicle data.'}
                </p>
              </div>

              {/* Optional message */}
              <div style={{ marginBottom: 16 }}>
                <label style={labelStyle}>Personal Message (optional)</label>
                <textarea
                  className="form-input"
                  placeholder="Hey, I added you to my garage..."
                  value={inviteMessage}
                  onChange={(e) => setInviteMessage(e.target.value)}
                  rows={2}
                  style={{ resize: 'vertical', minHeight: 48 }}
                />
              </div>

              {/* Buttons */}
              <div style={{ display: 'flex', gap: 10 }}>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowInviteModal(false)}
                  style={{ flex: 1 }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={inviteLoading || !inviteEmail.trim()}
                  style={{ flex: 1, opacity: inviteLoading ? 0.7 : 1 }}
                >
                  {inviteLoading ? 'Sending...' : 'Send Invite'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

/* ─── Shared Styles ─── */

const labelStyle = {
  display: 'block',
  fontSize: '0.6875rem',
  fontWeight: 600,
  color: 'var(--text-muted)',
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  marginBottom: 4,
};
